import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API } from '../utils/api';

const TermsOfServicePage = () => {
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/legal/terms-of-service`);
      if (response.data.success) {
        setContent(response.data.content);
        setLastUpdated(response.data.last_updated);
      }
    } catch (err) {
      console.error('Failed to fetch terms of service:', err);
      setError('Failed to load Terms of Service. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
          <p className="mt-4 text-gray-600">Loading Terms of Service...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/" className="text-yellow-600 hover:text-yellow-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <h1 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">Site</span>
                <span className="text-white">Orbit</span>
              </h1>
            </Link>
            <Link 
              to="/" 
              className="text-gray-300 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Page Title */}
      <div className="bg-gradient-to-b from-gray-100 to-gray-50 py-12 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Terms of Service</h1>
          <p className="text-gray-600">Please read these terms carefully before using SiteOrbit</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
          {/* Document Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-gray-800 
              prose-h1:hidden
              prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-gray-700
              prose-h3:text-lg prose-h3:font-medium prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-gray-700
              prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-ul:pl-6
              prose-li:text-gray-600 prose-li:mb-2
              prose-a:text-yellow-600 prose-a:hover:text-yellow-700 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-800
              prose-table:border-collapse prose-table:w-full
              prose-th:border prose-th:border-gray-200 prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left
              prose-td:border prose-td:border-gray-200 prose-td:p-3"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
          />
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p className="mb-4">Related Documents:</p>
          <div className="flex justify-center gap-6">
            <Link to="/privacy-policy" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Privacy Policy
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Home
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} SiteOrbit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
